import { formatPhone } from 'src/utils/formatInputs';
import { isSiteCanadian } from 'src/utils/helpers';
import { useTranslation } from 'react-i18next';
import { format, isValid, parseISO } from 'date-fns';

export interface IProgramEligibilityResult {
    accountNumber: string;
    accountType: string;
    vehicleDescription: string;
    vin: string;
    maturityDate: string;
    residual: number | null;
    basePayment: number | null;
    customerName: string;
    address: string[];
    telephone: string;
    programs: string[];
    salespersonCode: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const text = (value: unknown): string =>
    typeof value === 'string' ? value : '';

const amount = (value: unknown): number | null => {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    if (typeof value === 'string' && !value.trim()) return null;
    const result = Number(value);
    return Number.isFinite(result) ? result : null;
};

export const parseEligibilityResults = (
    data: unknown,
): IProgramEligibilityResult[] => {
    if (data === null) return [];
    const records = Array.isArray(data) ? data : [data];

    return records.map((record) => {
        if (!isRecord(record) || !text(record.accountNumber)) {
            throw new Error('Unsupported program eligibility response');
        }

        const programs = Array.isArray(record.programCodes)
            ? record.programCodes.filter(
                  (program: unknown): program is string =>
                      typeof program === 'string',
              )
            : [];

        return {
            accountNumber: text(record.accountNumber),
            accountType: text(record.productTypeCode),
            vehicleDescription: text(record.vehicleDescription),
            vin: text(record.vin),
            maturityDate: text(record.maturityDate),
            residual: amount(record.currentResidual),
            basePayment: amount(record.monthlyBasePayment),
            customerName:
                text(record.customerFullName) ||
                [text(record.customerFirst), text(record.customerLast)]
                    .filter(Boolean)
                    .join(' '),
            address: [
                text(record.garagingAddress1),
                text(record.garagingAddress2),
                [
                    text(record.garagingCity),
                    text(record.garagingState),
                    text(record.garagingPostalCode),
                ]
                    .filter(Boolean)
                    .join(' '),
            ].filter(Boolean),
            telephone: formatPhone(text(record.primaryHomePhone)),
            programs,
            salespersonCode: text(record.brandSpCode),
        };
    });
};

export const EligibilityResultsSkeleton = () => {
    const detail = (key: string) => (
        <div className='program-eligibility__detail' key={key}>
            <span className='program-eligibility__skeleton-line program-eligibility__skeleton-line--label' />
            <span className='program-eligibility__skeleton-line' />
        </div>
    );

    return (
        <div className='program-eligibility__result' aria-hidden='true'>
            <div className='program-eligibility__overview'>
                <div className='c-card program-eligibility__vehicle'>
                    <span className='program-eligibility__skeleton-line program-eligibility__skeleton-line--title' />
                    <div className='program-eligibility__vehicle-description'>
                        <span className='program-eligibility__skeleton-line' />
                    </div>
                    <div className='program-eligibility__vehicle-details'>
                        {detail('vin')}
                    </div>
                </div>
                <div className='c-card program-eligibility__contract'>
                    <div className='program-eligibility__contract-heading'>
                        <span className='program-eligibility__skeleton-line program-eligibility__skeleton-line--title' />
                    </div>
                    <div className='program-eligibility__maturity'>
                        {detail('maturity')}
                    </div>
                    <div className='program-eligibility__contract-values'>
                        {['residual', 'payment'].map(detail)}
                    </div>
                </div>
            </div>
            <div className='c-card program-eligibility__customer'>
                <span className='program-eligibility__skeleton-line program-eligibility__skeleton-line--title' />
                <div className='program-eligibility__customer-details'>
                    {[
                        'customer',
                        'account',
                        'type',
                        'phone',
                        'program',
                        'salesperson',
                    ].map(detail)}
                </div>
            </div>
        </div>
    );
};

const EligibilityResults = ({
    result,
}: {
    result: IProgramEligibilityResult;
}) => {
    const { t, i18n } = useTranslation();
    const label = (key: string) => t(`ProgramEligibility.${key}`);
    const missing = label('MissingValue');
    const currency = (value: number | null) =>
        value === null
            ? missing
            : new Intl.NumberFormat(i18n.language, {
                  style: 'currency',
                  currency: isSiteCanadian() ? 'CAD' : 'USD',
              }).format(value);
    const maturity = parseISO(result.maturityDate.split('T')[0]);
    const accountType = ['LEASE', 'LOAN'].includes(
        result.accountType.toUpperCase(),
    )
        ? label(result.accountType.toUpperCase() === 'LEASE' ? 'Lease' : 'Loan')
        : result.accountType || missing;

    const detail = (key: string, value: string | string[]) => (
        <div className='program-eligibility__detail'>
            <dt>{label(key)}</dt>
            <dd>
                {Array.isArray(value)
                    ? value.length
                        ? value.map((line, index) => (
                              <span key={`${index}-${line}`}>{line}</span>
                          ))
                        : missing
                    : value || missing}
            </dd>
        </div>
    );

    return (
        <article
            className='program-eligibility__result'
            aria-label={result.accountNumber}
        >
            <div className='program-eligibility__overview'>
                <section
                    className='c-card program-eligibility__vehicle'
                    aria-label={label('VehicleDetails')}
                >
                    <h2>{label('VehicleDetails')}</h2>
                    <p className='program-eligibility__vehicle-description'>
                        {result.vehicleDescription || missing}
                    </p>
                    <dl className='program-eligibility__vehicle-details'>
                        {detail('VIN', result.vin)}
                    </dl>
                </section>
                <section
                    className='c-card program-eligibility__contract'
                    aria-label={label('ContractSummary')}
                >
                    <div className='program-eligibility__contract-heading'>
                        <h2>{label('ContractSummary')}</h2>
                        <span className='program-eligibility__contract-type'>
                            {accountType}
                        </span>
                    </div>
                    <dl className='program-eligibility__maturity'>
                        {detail(
                            'MaturityDate',
                            isValid(maturity)
                                ? format(maturity, 'MM/dd/yyyy')
                                : missing,
                        )}
                    </dl>
                    <dl className='program-eligibility__contract-values'>
                        {detail('Residual', currency(result.residual))}
                        {detail('BasePayment', currency(result.basePayment))}
                    </dl>
                </section>
            </div>
            <section
                className='c-card program-eligibility__customer'
                aria-label={label('CustomerDetails')}
            >
                <h2>{label('CustomerDetails')}</h2>
                <dl className='program-eligibility__customer-details'>
                    {detail(
                        'CustomerNameAddress',
                        [result.customerName, ...result.address].filter(
                            Boolean,
                        ),
                    )}
                    {detail('Account', result.accountNumber)}
                    {detail('Type', accountType)}
                    {detail('Telephone', result.telephone)}
                    {detail('Program', result.programs)}
                    {detail('SalespersonCode', result.salespersonCode)}
                </dl>
            </section>
        </article>
    );
};

export default EligibilityResults;
